import { fetchApi } from "@/lib/api-helpers";
import { z } from "zod";

export const createMagicLinkResponseSchema = z.object({
  multi_factor_authentication: z.object({
    uuid: z.string(),
    authenticated: z.boolean(),
    verification_channel: z.string(),
    verification_code_expires_at: z.string(),
  }),
});

export type CreateMagicLinkResponse = z.infer<
  typeof createMagicLinkResponseSchema
>;

const createMagicLinkInputSchema = z.object({
  email: z.string().email(),
  fluid_shop: z.string(),
  cart_token: z.string(),
  lookup_only: z.boolean().optional(),
});

export type CreateMagicLinkInput = z.infer<typeof createMagicLinkInputSchema>;

export async function createMagicLink(input: CreateMagicLinkInput) {
  return fetchApi({
    endpoint: `/carts/${input.cart_token}/magic_link`,
    method: "POST",
    input,
    inputSchema: createMagicLinkInputSchema,
    outputSchema: createMagicLinkResponseSchema,
  });
}

export async function resendMagicLink(uuid: string) {
  return fetchApi({
    endpoint: `/authentication/resend_mfa?uuid=${encodeURIComponent(uuid)}`,
    method: "POST",
    outputSchema: createMagicLinkResponseSchema,
  });
}

const confirmMagicLinkInputSchema = z.object({
  uuid: z.string(),
  multi_factor_authentication: z.object({
    verification_code: z.string(),
  }),
});

export type ConfirmMagicLinkInput = z.infer<typeof confirmMagicLinkInputSchema>;

const confirmMagicLinkResponseSchema = z.object({
  jwt: z.string(),
  authenticated: z.boolean(),
  auth_type: z.string(),
  meta: z.object({
    request_id: z.string(),
    timestamp: z.string(),
  }),
});

export type ConfirmMagicLinkResponse = z.infer<
  typeof confirmMagicLinkResponseSchema
>;

export async function confirmMagicLink(
  cartToken: string,
  input: ConfirmMagicLinkInput,
) {
  return fetchApi({
    endpoint: `/carts/${cartToken}/confirm_magic_link`,
    method: "POST",
    input,
    inputSchema: confirmMagicLinkInputSchema,
    outputSchema: confirmMagicLinkResponseSchema,
  });
}

const customerCompanySchema = z.object({
  id: z.number(),
  name: z.string(),
  icon_url: z.string().nullable(),
  logo_url: z.string().nullable(),
  fluid_shop: z.string(),
  jwt: z.string(),
});

export type CustomerCompany = z.infer<typeof customerCompanySchema>;

const customerCompaniesResponseSchema = z.object({
  companies: z.array(customerCompanySchema),
  current_company_id: z.number().nullable(),
  meta: z.object({
    request_id: z.string(),
    timestamp: z.string(),
  }),
});

export type CustomerCompaniesResponse = z.infer<
  typeof customerCompaniesResponseSchema
>;

export async function fetchCustomerCompanies(jwt: string) {
  return fetchApi({
    endpoint: `/authentication/customer/companies`,
    method: "GET",
    outputSchema: customerCompaniesResponseSchema,
    fetchOptions: {
      headers: {
        "Content-Type": "application/json",
        "x-fluid-client": "checkout",
        Authorization: `Bearer ${jwt}`,
      },
    },
  });
}
